import { HStack, useToast, VStack } from '@chakra-ui/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { useImmer } from 'use-immer';

import { useGetDepartmentsQuery } from '@/API/departments.api';
import DepartmentsDropdown from '@/components/departments-dropdown/DepartmentsDropdown';
import PersonnelTypeDropdown from '@/components/personnel-types-dropdown/PersonnelTypeDropdown';
import PublicURLControls from '@/components/view-access-drawer/PublicURLControls';
import { FEATURE_FLAGS_ENUM } from '@/constants/config';
import { DEPARTMENTS_LOAD_ERROR_MESSAGE } from '@/constants/defaults';
import { FULL_WIDTH, TOP_BAR_Z_INDEX, ZERO_SPACE } from '@/constants/ui';
import useDepartmentsRetryOnMountError from '@/hooks/useDepartmentsRetryOnMountError';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  setViewAccessIsDepartmentListClosed,
  setViewAccessPersonnelTypeIds,
  setViewAccessSelectedDepartmentIds,
} from '@/store/slices/viewAccess.slice';
import { DepartmentSlim } from '@/types/department.types';
import { PersonnelType } from '@/types/personnel.types';

/**
 * Chakra's toastStore.notify appends unconditionally, so an id does not replace an existing toast on its own -
 * it makes one addressable by toast.isActive. Kept local to this component rather than exported so the other
 * department dropdowns cannot suppress each other's notifications through a shared key.
 */
const DEPARTMENTS_LOAD_ERROR_TOAST_ID = 'view-access-departments-load-error';
const DEPARTMENTS_EXCLUDING_MRC_LOAD_ERROR_TOAST_ID = 'view-access-departments-excluding-mrc-load-error';

interface ViewAccessControlsProps {
  actionButtons: React.ReactNode;
  personnelTypes: PersonnelType[];
}

interface UIState {
  isDepartmentListOpen: boolean;
  searchValue: string;
  selectedDropdownDepartmentIds: number[];
}

const ViewAccessControls = (props: ViewAccessControlsProps) => {
  const { actionButtons, personnelTypes } = props;

  const dispatch = useAppDispatch();
  const toast = useToast();
  const flags = useFlags();

  const isNoDepartmentMRCEnabled = flags[FEATURE_FLAGS_ENUM.LV15455NVMNoDepartmentMRC];

  const [uiState, updateUIState] = useImmer<UIState>({
    isDepartmentListOpen: false,
    searchValue: '',
    selectedDropdownDepartmentIds: [],
  });

  /**
   * The department tree is fetched once per session at app mount (App.tsx -> useDepartments) and this hook
   * subscribes to that same cache entry - RTK Query keys by serialized argument, so taking no argument here
   * reuses it and opening the drawer costs no request. That subscription is read instead of
   * state.departmentsAndTemplates because the slice carries the list but not the loading or error state of the
   * fetch which fills it, and the dropdown needs both. Kept subscribed unconditionally (not just when the flag
   * is off) so this remains the retry point for the app-wide entry regardless of the flag - see the effect below.
   */
  const {
    data: departmentsData,
    isError: isDepartmentsError,
    isFetching: isFetchingDepartments,
    isUninitialized,
    refetch: refetchDepartments,
  } = useGetDepartmentsQuery();

  /**
   * Behind the flag, the drawer asks the same endpoint to exclude MRC server-side instead of filtering it out
   * of the shared list client-side. That argument makes this a second, separate cache entry - skipped entirely
   * while the flag is off so nothing changes for the shared entry or its callers.
   */
  const {
    data: departmentsExcludingMRCData,
    isError: isDepartmentsExcludingMRCError,
    isFetching: isFetchingDepartmentsExcludingMRC,
    isUninitialized: isDepartmentsExcludingMRCUninitialized,
    refetch: refetchDepartmentsExcludingMRC,
  } = useGetDepartmentsQuery({ excludeMRC: true }, { skip: !isNoDepartmentMRCEnabled });

  // Retried unconditionally so this stays the retry point for the app-wide entry's other consumers even
  // when the flag is on, but only toasted when this drawer's own dropdown is actually the one showing it -
  // otherwise a shared-entry failure would toast "Unable to load departments" over a dropdown that loaded
  // fine from the excludeMRC entry.
  useDepartmentsRetryOnMountError(
    isDepartmentsError,
    refetchDepartments,
    toast,
    DEPARTMENTS_LOAD_ERROR_TOAST_ID,
    !isNoDepartmentMRCEnabled,
  );
  useDepartmentsRetryOnMountError(
    isNoDepartmentMRCEnabled && isDepartmentsExcludingMRCError,
    refetchDepartmentsExcludingMRC,
    toast,
    DEPARTMENTS_EXCLUDING_MRC_LOAD_ERROR_TOAST_ID,
  );

  const isActiveDepartmentsError = isNoDepartmentMRCEnabled ? isDepartmentsExcludingMRCError : isDepartmentsError;

  // A query which has not started yet reports isFetching false. Count uninitialized as loading too so the
  // dropdown cannot flash enabled over an empty list.
  const isDepartmentsLoading = isNoDepartmentMRCEnabled
    ? isDepartmentsExcludingMRCUninitialized || isFetchingDepartmentsExcludingMRC
    : isUninitialized || isFetchingDepartments;

  const departments = React.useMemo<DepartmentSlim[]>(() => {
    const activeDepartmentsData = isNoDepartmentMRCEnabled ? departmentsExcludingMRCData : departmentsData;

    if (!activeDepartmentsData) return [];

    return activeDepartmentsData.map((dept) => ({
      departmentId: dept.department_id,
      departmentName: dept.name,
    }));
  }, [departmentsData, departmentsExcludingMRCData, isNoDepartmentMRCEnabled]);

  const { selectedPersonnelTypeIds } = useAppSelector((state) => state.viewAccess);

  const personnelTypeOptions = React.useMemo(() => {
    return personnelTypes.map((personnelType) => {
      return {
        label: personnelType.name,
        value: personnelType.ptypeId,
      };
    });
  }, [personnelTypes]);

  const handlePersonnelTypesChange = React.useCallback(
    (selectedPersonnelTypeIds: number[]) => {
      dispatch(setViewAccessPersonnelTypeIds(selectedPersonnelTypeIds));
    },
    [dispatch],
  );

  const handleDepartmentsChange = React.useCallback(
    (selectedIds: number[]) => {
      updateUIState((draft) => {
        draft.selectedDropdownDepartmentIds = selectedIds;
      });
    },
    [updateUIState],
  );

  const handleDepartmentsListClose = React.useCallback(
    (selectedIds: number[]) => {
      dispatch(setViewAccessSelectedDepartmentIds(selectedIds));
      dispatch(setViewAccessIsDepartmentListClosed(true));
    },
    [dispatch],
  );

  return (
    <HStack
      align={'flex-end'}
      bg={'#fff'}
      gap={2}
      justify={'space-between'}
      position={'sticky'}
      top={ZERO_SPACE}
      width={FULL_WIDTH}
      zIndex={TOP_BAR_Z_INDEX}
    >
      <VStack width={FULL_WIDTH}>
        <HStack align={'flex-end'} justify={'space-between'} width={FULL_WIDTH}>
          <HStack>
            <DepartmentsDropdown
              departmentChangeHandler={handleDepartmentsChange}
              departmentList={departments}
              departmentListCloseHandler={handleDepartmentsListClose}
              isLoading={isDepartmentsLoading}
              noOptionsMessage={isActiveDepartmentsError ? DEPARTMENTS_LOAD_ERROR_MESSAGE : undefined}
              selectedIds={uiState.selectedDropdownDepartmentIds}
            />
            <PersonnelTypeDropdown
              availablePersonnelTypes={personnelTypeOptions}
              onChangeHandler={handlePersonnelTypesChange}
              selectedPersonnelTypeIds={selectedPersonnelTypeIds}
            />
          </HStack>
          {actionButtons}
        </HStack>
        <PublicURLControls />
      </VStack>
    </HStack>
  );
};

export default ViewAccessControls;
